import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { toast } from 'react-toastify';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Alert, Button } from 'antd';
import FadeIn from '../FadeIn';
import { PaymentMethods } from '../../services/exports/Constants';
import PaymentMethodForm from '../billing/PaymentMethodForm';
import LoadingIndicator from '../LoadingIndicator';
import useApiClient from '../../hooks/api/useApiClient';
import useQueryParams from '../../hooks/utility/useQueryParams';
import PaymentInfo from '../billing/PaymentInfo';

export default function PaymentInfoStep(props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:PaymentInfoStep' });

  const { CurrentCompanyManager, StripeManager } = useApiClient();
  const query = useQueryParams();

  const { moveForward } = props;

  const { company } = useSelector((store) => store.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const merchant = company?.merchant ?? {};
  const { payment_options } = merchant;
  const paymentOption = collect(payment_options).first();

  // The key of the provider's Stripe account row; the flat `stripe_key` only
  // comes from a backend that predates Stripe accounts as rows of their own.
  const publishableKey = serviceProvider.stripe_publishable_key ?? serviceProvider.stripe_key;

  // Partners without payment configuration have no publishable key yet —
  // loadStripe(null) would render a broken Stripe element.
  const stripe = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);

  const [clientSecret, setClientSecret] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState(null);

  const [changingPaymentMethod, setChangingPaymentMethod] = useState(
    query.has('checkout_session_id') || query.has('payment_method_id'),
  );
  const [loading, setLoading] = useState(query.has('checkout_session_id'));
  const [mounted, setMounted] = useState(false);

  const returnUrl = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('payment_method_id', paymentMethodId);

    const url = new URL(window.location.href);
    url.search = searchParams.toString();

    return url.toString();
  }, [paymentMethodId]);

  useEffect(() => {
    setTimeout(() => mount(), query.has('checkout_session_id') ? 5000 : 10);
  }, []);

  useEffect(() => {
    if (stripe && mounted && (!paymentOption || changingPaymentMethod)) {
      loadSetupIntent();
    }
  }, [stripe, mounted, paymentOption, changingPaymentMethod]);

  const mount = async () => {
    if (!query.has('checkout_session_id')) {
      return setMounted(true);
    }

    const { success, data } = await CurrentCompanyManager.get();

    if (!success) {
      return toast.error(t('toasts.failed_to_init_payment_setup'));
    }

    if (data?.merchant?.payment_options?.length > 0) {
      toast.success(t('toasts.successfully_saved_payment_method'));
      await onSuccess();
    }

    setMounted(true);
  };

  const loadSetupIntent = async () => {
    setLoading(true);
    const { success, data } = await StripeManager.createSetupIntent({
      payment_methods: ['card', 'sepa_debit'],
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_init_payment_setup'));
    }

    return setClientSecret(data.client_secret);
  };

  const onSuccess = async () => {
    CurrentCompanyManager.get();
    moveForward && moveForward();
    window.location.replace(window.location.pathname);
  };

  const paymentMethodName = () =>
    paymentOption.payment_method_id === PaymentMethods.CARD ? t('labels.card') : t('labels.bank_account');

  if (!stripe) {
    return (
      <FadeIn>
        <Alert type="info" showIcon message={t('alerts.payments_not_configured')} />
      </FadeIn>
    );
  }

  if (loading) {
    return (
      <div className="mt-lg">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <FadeIn>
      {paymentOption && !changingPaymentMethod && (
        <div className="flex justify-between">
          <h5 className="my-auto">
            {paymentMethodName()} ****
            {paymentOption.payment_method_details}
          </h5>
          <Button onClick={() => setChangingPaymentMethod(true)} className="my-auto">
            {t('buttons.change_payment_method')}
          </Button>
        </div>
      )}
      {(!paymentOption || changingPaymentMethod) && clientSecret && (
        <>
          <PaymentInfo />
          <hr className="my-xl" />
          <Elements stripe={stripe} options={{ clientSecret }}>
            <PaymentMethodForm
              paymentMethodId={paymentMethodId}
              returnUrl={returnUrl}
              buttonText={t('buttons.save_payment_info')}
              onSelectPaymentMethod={setPaymentMethodId}
              onSuccess={onSuccess}
            />
          </Elements>
        </>
      )}
    </FadeIn>
  );
}
